import { ref, onValue, set, onDisconnect } from 'firebase/database';
import { rtdb } from '../lib/firebase';

// Tracks the active uid so visibility/beforeunload handlers know who to mark offline
let _activeUid = null;

/**
 * Writes { state, last_changed } to status/{uid} in RTDB.
 * Exported so logout can call it directly.
 */
export const setPresenceStatus = (uid, state) => {
  if (!uid) return Promise.resolve();
  return set(ref(rtdb, `status/${uid}`), {
    state,
    last_changed: Date.now(),
  });
};

/**
 * Sets up live presence for the current user in Firebase Realtime Database.
 *
 * Uses the special `.info/connected` path which Firebase updates automatically
 * when the client connects or disconnects — even on browser tab close / crash.
 *
 * Also registers `visibilitychange` and `beforeunload` listeners so the user
 * is marked offline immediately when the browser tab is hidden or closed,
 * without waiting for a TCP timeout.
 *
 * Path: status/{uid} — mirrors the Admin portal's status/${uid} path.
 * NOTE: Both platforms use the same RTDB, so the Admin's usePresence hook
 * can read status written by the PWA and vice versa.
 *
 * @param {string} uid - The authenticated Firebase user's UID.
 */
export const setupPresence = (uid) => {
  if (!uid) return;

  _activeUid = uid;

  const connectedRef = ref(rtdb, '.info/connected');
  const statusRef    = ref(rtdb, `status/${uid}`);

  onValue(connectedRef, async (snapshot) => {
    if (snapshot.val() === false) return;

    // Register what should happen when this client disconnects (server-side)
    await onDisconnect(statusRef).set({
      state:        'offline',
      last_changed: Date.now(),
    });

    // Immediately mark as online
    await set(statusRef, {
      state:        'online',
      last_changed: Date.now(),
    });

    console.log(`[Presence] ✅ User ${uid} marked online`);
  });
};

/**
 * Marks the current user offline immediately when the browser tab is hidden
 * or closed. Uses `visibilitychange` (tab switch / minimize) and
 * `beforeunload` (tab/window close). These supplement onDisconnect so the
 * status change is instant rather than waiting for a TCP timeout.
 *
 * Call this once — e.g. in AuthContext after setupPresence.
 */
export const setupTabCloseHandlers = () => {
  const markOffline = () => {
    if (!_activeUid) return;
    setPresenceStatus(_activeUid, 'offline').catch(() => {});
  };

  // Remove any previous listener to avoid duplicates on re-login
  window.removeEventListener('beforeunload', markOffline);

  // Only mark offline when the tab is actually closed — NOT on hide/minimize
  window.addEventListener('beforeunload', markOffline);
};

/**
 * Subscribes to a user's online/offline status.
 * Returns an unsubscribe function — call it on component unmount.
 *
 * @param {string} uid      - The UID to watch.
 * @param {function} callback - Called with { state, last_changed } or { state: 'offline' }.
 */
export const subscribeToPresence = (uid, callback) => {
  if (!uid) return () => {};

  const statusRef = ref(rtdb, `status/${uid}`);
  return onValue(statusRef, (snapshot) => {
    callback(snapshot.val() || { state: 'offline' });
  });
};
